import { useState } from "react";
import { X, DownloadSimple, Camera } from "@phosphor-icons/react";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SnapshotModal({ lat, lon, date, layerName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const bbox = `${(lon - 3).toFixed(2)},${(lat - 3).toFixed(2)},${(lon + 3).toFixed(2)},${(lat + 3).toFixed(2)}`;
  const snapshotUrl = `${API}/nasa/snapshot?layers=${layerName}&date=${date}&bbox=${bbox}&width=1024&height=768`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[2000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="snapshot-modal-overlay"
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-stone-900 rounded-2xl border border-stone-700 overflow-hidden max-w-4xl w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="snapshot-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-800">
          <div className="flex items-center gap-2">
            <Camera size={18} weight="bold" className="text-emerald-400" />
            <span className="text-sm font-heading font-medium text-white">
              NASA Worldview Snapshot
            </span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={snapshotUrl}
              download={`conexus_snapshot_${date}.png`}
              className="text-stone-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-stone-800"
              data-testid="snapshot-download-btn"
              title="Download snapshot"
            >
              <DownloadSimple size={18} />
            </a>
            <button
              onClick={onClose}
              className="text-stone-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-stone-800"
              data-testid="snapshot-close-btn"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Image */}
        <div className="relative bg-stone-950 min-h-[200px]">
          {loading && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <div className="w-8 h-8 border-2 border-stone-600 border-t-emerald-400 rounded-full animate-spin" />
              <p className="text-xs text-stone-500 font-body">Loading NASA imagery...</p>
            </div>
          )}
          {error ? (
            <div className="h-64 flex flex-col items-center justify-center text-stone-500 gap-2">
              <Camera size={32} className="text-stone-600" />
              <p className="text-sm">Failed to load snapshot</p>
              <p className="text-xs">Try a different date or location</p>
            </div>
          ) : (
            <img
              src={snapshotUrl}
              alt="NASA Worldview Snapshot"
              className={`w-full transition-opacity duration-300 ${loading ? "opacity-0" : "opacity-100"}`}
              onLoad={() => setLoading(false)}
              onError={() => {
                setLoading(false);
                setError(true);
              }}
              data-testid="snapshot-image"
            />
          )}
        </div>

        {/* Footer info */}
        <div className="px-5 py-3 border-t border-stone-800 flex flex-wrap items-center gap-x-6 gap-y-1 text-[11px] text-stone-500 font-body">
          <span>
            Lat: {lat.toFixed(4)}, Lon: {lon.toFixed(4)}
          </span>
          <span>Date: {date}</span>
          <span>Layer: {layerName}</span>
        </div>
      </motion.div>
    </motion.div>
  );
}
